import * as yup from 'yup';

/**
 * E43 · Health Data — the both-ends input codecs of the health-data mutations (plan 03 §2.1 #4): the phone (before it
 * sends) and — as the shield's inputRule — the API run the SAME schema. SHAPE only: whether a type is known, chosen
 * or still shared is the decider's (HEALTH_TYPES_EMPTY · HEALTH_TYPE_UNKNOWN · HEALTH_TYPE_NOT_GRANTED, the board's
 * coded refusals in the person's language), so the wire answers with the board's code instead of a generic
 * validation error. `subject` is ABSENT on purpose: the caller.
 *
 * The schemas are typed `yup.AnyObjectSchema` on purpose: their inferred nested types tipped `getValidators`' return
 * type past what the compiler serialises (TS7056); the wire types come from the GraphQL codegen anyway.
 *
 * Bounds keep a request honest without deciding anything: a read window covers at most ~a month and a half (the
 * gear's first read is the last 30 days), one translation at most 62 nights / days, a workout list 50.
 */
const uuid = yup
  .string()
  .trim()
  .matches(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i)
  .required();

const day = yup
  .string()
  .trim()
  .matches(/^\d{4}-\d{2}-\d{2}$/)
  .required();

const minutes = yup.number().integer().min(0).max(24 * 60 * 2);
const types = yup.array().of(yup.string().trim().max(32).required()).max(8).required();

export const HEALTH_SOURCE_VALUES = ['appleHealth', 'healthConnect'] as const;

export const grantHealthDataAccessInputSchema: yup.AnyObjectSchema = yup.object({
  types,
  source: yup.string().oneOf([...HEALTH_SOURCE_VALUES]).required(),
  disclosureVersion: yup.string().trim().min(1).max(64).required(),
});

export const revokeHealthDataAccessInputSchema: yup.AnyObjectSchema = yup.object({
  types: types.min(1),
  deleteImported: yup.boolean().required(),
});

const windowSchema = yup.object({
  focus: yup
    .object({
      type: yup.string().trim().max(32).required(),
      ref: yup.string().trim().max(128).required(),
    })
    .required(),
  from: yup.date().required(),
  to: yup.date().required(),
});

export const requestHealthReadInputSchema: yup.AnyObjectSchema = yup.object({
  requestId: uuid,
  types: types.min(1),
  from: yup.date().required(),
  to: yup.date().required(),
  windows: yup.array().of(windowSchema.required()).max(20).nullable(),
});

const nightSchema = yup.object({
  night: day,
  inBedStart: yup.date().required(),
  inBedEnd: yup.date().required(),
  asleepMinutes: minutes.required(),
  awakenings: yup.number().integer().min(0).max(500).required(),
  stages: yup
    .object({
      deep: minutes.required(),
      core: minutes.required(),
      rem: minutes.required(),
      awake: minutes.required(),
    })
    .nullable()
    .default(null),
});

const workoutSchema = yup.object({
  type: yup.string().trim().max(32).required(),
  start: yup
    .string()
    .trim()
    .matches(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)
    .required(),
  minutes: minutes.required(),
});

const activityDaySchema = yup.object({
  date: day,
  stepCount: yup.number().integer().min(0).max(1_000_000).required(),
  activeMinutes: minutes.required(),
  workouts: yup.array().of(workoutSchema.required()).max(50).required(),
});

export const HRV_METRIC_VALUES = ['sdnn', 'rmssd'] as const;

const heartRateDaySchema = yup.object({
  date: day,
  restingHeartRate: yup.number().integer().min(20).max(250).nullable(),
  hrv: yup
    .object({
      value: yup.number().min(0).max(1000).required(),
      metric: yup.string().oneOf([...HRV_METRIC_VALUES]).required(),
    })
    .nullable()
    .default(null),
});

const bpm = yup.number().integer().min(20).max(250);

const heartRateWindowSchema = yup.object({
  focus: yup
    .object({
      type: yup.string().trim().max(32).required(),
      ref: yup.string().trim().max(128).required(),
    })
    .required(),
  from: yup.date().required(),
  to: yup.date().required(),
  bpm: yup
    .object({
      min: bpm.required(),
      max: bpm.required(),
      mean: bpm.required(),
      samples: yup.number().integer().min(0).max(1_000_000).required(),
    })
    .required(),
});

export const translateHealthSummariesInputSchema: yup.AnyObjectSchema = yup.object({
  requestId: uuid,
  source: yup.string().oneOf([...HEALTH_SOURCE_VALUES]).required(),
  nights: yup.array().of(nightSchema.required()).max(62).required(),
  activityDays: yup.array().of(activityDaySchema.required()).max(62).required(),
  heartRateDays: yup.array().of(heartRateDaySchema.required()).max(62).nullable(),
  heartRateWindows: yup.array().of(heartRateWindowSchema.required()).max(50).nullable(),
});

export const setHealthDataSharingInputSchema: yup.AnyObjectSchema = yup.object({
  performer: yup
    .string()
    .trim()
    .matches(/^users\/[A-Za-z0-9_-]{1,64}$/)
    .required(),
  sleep: yup.boolean().required(),
  activity: yup.boolean().required(),
  heartRate: yup.boolean().required(),
});

/** The mutation ARGUMENTS — what the shield's inputRules validate. */
export const grantHealthDataAccessArgsSchema: yup.AnyObjectSchema = yup.object({
  input: grantHealthDataAccessInputSchema.required(),
});
export const revokeHealthDataAccessArgsSchema: yup.AnyObjectSchema = yup.object({
  input: revokeHealthDataAccessInputSchema.required(),
});
export const requestHealthReadArgsSchema: yup.AnyObjectSchema = yup.object({
  input: requestHealthReadInputSchema.required(),
});
export const translateHealthSummariesArgsSchema: yup.AnyObjectSchema = yup.object({
  input: translateHealthSummariesInputSchema.required(),
});

export const setHealthDataSharingArgsSchema: yup.AnyObjectSchema = yup.object({
  input: setHealthDataSharingInputSchema.required(),
});

export const healthDataValidators = {
  setHealthDataSharingInputSchema,
  setHealthDataSharingArgsSchema,
  grantHealthDataAccessInputSchema,
  revokeHealthDataAccessInputSchema,
  requestHealthReadInputSchema,
  translateHealthSummariesInputSchema,
  grantHealthDataAccessArgsSchema,
  revokeHealthDataAccessArgsSchema,
  requestHealthReadArgsSchema,
  translateHealthSummariesArgsSchema,
};
